import { PrismicRichText } from '@prismicio/react';
import { RichText } from 'prismic-reactjs';
import { ButtonInline } from 'remote-com-shared/components/Button';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fieldHasContent } from 'remote-com-shared/domains/prismic/helpers';
import { fluidProp } from 'remote-com-shared/style/helpers';
import { AnimatedSection, sharedTransition } from 'remote-com-shared/style/motion';
import styled, { css } from 'styled-components';

import { OverviewSectionRow, Wrapper } from './OverviewSectionRow';

import { tabletBreakpoint } from '@/src/domains/shared/breakpoints';
import { defaultAnimation } from '@/src/style/motion';

export const OverviewSectionWrapper = styled.section`
  color: var(--colors-bayoux);
  ${fluidProp('padding-top', '0px', '10px')}
  ${fluidProp('padding-bottom', '80px', '130px')}

  ${({ $tone }) => {
    switch ($tone) {
      case 'light':
      case 'white':
        return css`
          background-image: var(--colors-blank);
        `;
      case 'zircon':
      default:
        return css`
          background-image: var(--colors-zircon);
        `;
    }
  }}
`;

export const OverviewSectionHeader = styled.header`
  text-align: center;
  ${fluidProp(['padding-top'], '60px', '160px')}
  ${fluidProp(['padding-bottom'], '60px', '100px')}

  ${defaultAnimation}

  & + ${Wrapper}:not(:only-child):nth-of-type(1) {
    margin-top: 0;
  }
`;

export const OverviewLabel = styled.div`
  margin: 0 auto 18px auto;
  text-align: ${({ $textAlign = 'center' }) => $textAlign};

  & > small {
    ${({ theme }) => theme.typography.subHead}
    text-transform: uppercase;
    color: var(--colors-irisBlue);
  }
`;

export const OverviewTitleComponent = styled.div`
  ${({ theme }) => theme.typography.scale3}
  ${fluidProp(['font-size'], '24px', '42px')}
  color: var(--colors-darkBlue);
  text-align: center;
  max-width: 650px;
  margin: 0 auto;

  ${({ $textAlign }) =>
    $textAlign === 'left' &&
    css`
      max-width: 500px;
      text-align: left;
      margin: 0;
    `}

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: inherit;
  }
`;

export const OverviewDescriptionComponent = styled.div`
  ${({ theme }) => theme.typography.bodyLead}
  margin: 0 auto;
  text-align: center;
  ${fluidProp(['max-width'], '320px', '750px')}

  ${({ $textAlign }) =>
    $textAlign === 'left' &&
    css`
      max-width: 500px;
      text-align: left;
      margin: 0;
    `}

    > * {
    font-size: inherit;
    font-weight: inherit;
  }
`;

export const OverviewCTALinksContainer = styled.div`
  display: flex;
  gap: 50px;
  justify-content: center;

  ${tabletBreakpoint(css`
    flex-direction: column;
    gap: 0px;
  `)}
`;

export const OverviewCTALinkText = styled(ButtonInline)``;

const LinkWrapper = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;

  & > svg {
    position: relative;
    top: -1px;
    ${sharedTransition('transform')};
    vertical-align: middle;

    & > g > circle {
      ${sharedTransition('stroke-dashoffset')};
      stroke-dasharray: 95;
      stroke-dashoffset: 95;
    }
  }

  &:hover {
    & > svg {
      transform: translate3d(5px, 0, 0);

      & > g > circle {
        stroke-dashoffset: 0;
      }
    }
  }

  ${tabletBreakpoint(css`
    justify-content: center;
  `)}
`;

const OverviewCTALinkArrow = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
    <g fill="none" stroke="#624DE3" strokeWidth="2" strokeLinejoin="round" strokeMiterlimit="10">
      <circle cx="16" cy="16" r="15.12" />
      <path d="M16.14 9.93L22.21 16l-6.07 6.07M8.23 16h13.98" />
    </g>
  </svg>
);

export const OverviewCTALinkWrapper = ({ children }) => (
  <LinkWrapper>
    {children} <OverviewCTALinkArrow />
  </LinkWrapper>
);

export const OverviewSectionNote = styled(ContentContainer)`
  ${({ theme }) => theme.typography.bodyXS}
  color: var(--colors-bayoux);
  margin: 0 auto;
  padding: 80px 30px;
`;

export function OverviewSection({
  overviewLabel,
  overviewTitle,
  overviewDescription,
  overviewSections = [],
  CTAButton,
  overviewCTALinks,
  note,
  directionOffset = false,
  bigImage,
  textAlign,
  ...props
}) {
  return (
    <>
      <OverviewSectionWrapper {...props}>
        {!overviewLabel && !overviewTitle && !overviewDescription ? null : (
          <AnimatedSection ComponentName={OverviewSectionHeader}>
            <ContentContainer>
              {fieldHasContent(overviewLabel) && (
                <OverviewLabel $textAlign={textAlign}>
                  <small>{overviewLabel}</small>
                </OverviewLabel>
              )}
              {fieldHasContent(overviewTitle) && (
                <OverviewTitleComponent $textAlign={textAlign}>
                  <PrismicRichText field={overviewTitle} />
                </OverviewTitleComponent>
              )}
              {fieldHasContent(overviewDescription) && (
                <OverviewDescriptionComponent $textAlign={textAlign}>
                  <PrismicRichText field={overviewDescription} />
                </OverviewDescriptionComponent>
              )}
            </ContentContainer>
          </AnimatedSection>
        )}

        {overviewSections.map((section, index) => (
          <OverviewSectionRow
            {...{ index, CTAButton, directionOffset, bigImage }}
            {...section}
            key={index}
          />
        ))}

        {overviewCTALinks && overviewCTALinks.length ? (
          <OverviewCTALinksContainer>
            {overviewCTALinks.map((item, index) => (
              <OverviewCTALinkWrapper key={index}>
                <RichText
                  render={item.link_text}
                  Component={OverviewCTALinkText}
                  href={item.link.url}
                  rel="noreferrer noopener"
                  target="_blank"
                />
              </OverviewCTALinkWrapper>
            ))}
          </OverviewCTALinksContainer>
        ) : null}

        {note && <OverviewSectionNote>{note}</OverviewSectionNote>}
      </OverviewSectionWrapper>
    </>
  );
}
